import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, fetchApi } from "@/api/fetchApi";
import { API_BASE } from "@/config/build";

/**
 * The blog API (plain JSON, unchanged by the redesign; backend src/modules/blog/api/router.ts).
 * Public reads go out without a token; the admin endpoints need the `admin` or `blogAdmin` claim.
 */
export type BlogPost = {
  id?: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  contentMd: string;
  coverStyle?: string | null;
  coverImageUrl?: string | null;
  published: boolean;
  publishedAt?: string | null;
  updatedAt?: string;
  createdAt?: string;
  authorName: string;
};

export type BlogPostInput = {
  title: string;
  slug?: string;
  authorName: string;
  excerpt: string;
  category: string;
  coverStyle: string;
  coverImageUrl: string;
  contentMd: string;
  published: boolean;
};

export const blogKeys = {
  published: ["blog", "published"] as const,
  post: (slug: string) => ["blog", "post", slug] as const,
  admin: ["blog", "admin"] as const,
};

const enc = encodeURIComponent;

export function usePublishedPosts() {
  return useQuery({
    queryKey: blogKeys.published,
    queryFn: async () => {
      const posts = await fetchApi<BlogPost[]>("/blog", { anonymous: true });
      return Array.isArray(posts) ? posts : [];
    },
  });
}

/** A published post, or null when there's no such post (404). */
export function usePost(slug: string) {
  return useQuery({
    queryKey: blogKeys.post(slug),
    queryFn: async () => {
      try {
        return (await fetchApi<BlogPost>(`/blog/${enc(slug)}`, { anonymous: true })) ?? null;
      } catch (e) {
        if (e instanceof ApiError && e.isNotFound) return null;
        throw e;
      }
    },
  });
}

/** Every post, drafts included (admins and blog authors). */
export function useAllPosts(enabled = true) {
  return useQuery({
    queryKey: blogKeys.admin,
    enabled,
    queryFn: async () => {
      const posts = await fetchApi<BlogPost[]>("/blog/admin/all");
      return Array.isArray(posts) ? posts : [];
    },
  });
}

function useInvalidateBlog() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["blog"] });
}

export function useSavePost() {
  const invalidate = useInvalidateBlog();
  return useMutation({
    mutationFn: ({ slug, post }: { slug?: string; post: BlogPostInput }) =>
      slug
        ? fetchApi<BlogPost>(`/blog/${enc(slug)}`, { method: "PUT", json: post })
        : fetchApi<BlogPost>("/blog", { method: "POST", json: post }),
    onSuccess: invalidate,
  });
}

export function useDeletePost() {
  const invalidate = useInvalidateBlog();
  return useMutation({
    mutationFn: (slug: string) => fetchApi(`/blog/${enc(slug)}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

/** Cover upload: raw image bytes with their own Content-Type; answers `{ url }`. */
export async function uploadCoverImage(file: File, token: string | null): Promise<string> {
  const res = await fetch(`${API_BASE.replace(/\/$/, "")}/blog/media`, {
    method: "POST",
    headers: {
      "Content-Type": file.type,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: file,
  });
  if (!res.ok) {
    let message = "Image upload failed.";
    try {
      const err = (await res.json()) as { message?: string; error?: string | { message?: string } };
      message =
        err.message || (typeof err.error === "string" ? err.error : err.error?.message) || message;
    } catch {
      // not JSON; keep the default
    }
    throw new Error(message);
  }
  const body = (await res.json()) as { url: string };
  return body.url;
}
